import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../context/LanguageContext";
import { FaPills, FaPlus } from "react-icons/fa";
import { DataGridTable, SearchBar, FilterSelect, FiltersContainer } from "../components";
import { UserContext } from "../context/UserContext";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import "../styles/common.css";
import "./ProduitsPharmaceutiques.css";

export default function ProduitsPharmaceutiques() {
  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  
  const TYPE_PRODUIT_OPTIONS = [
    { value: 'vaccin', label: t.vaccine },
    { value: 'vermifuge', label: t.dewormer },
    { value: 'antibiotique', label: t.antibiotic },
    { value: 'vitamine', label: t.vitamin },
    { value: 'desinfectant', label: t.disinfectant },
    { value: 'autre', label: t.other },
  ];

  const UNITE_OPTIONS = [
    { value: 'ml', label: 'ml' },
    { value: 'comprime', label: t.tablet },
    { value: 'flacon', label: t.bottle },
    { value: 'dose', label: t.dose },
    { value: 'kg', label: 'kg' },
    { value: 'g', label: 'g' },
    { value: 'unite', label: t.unit },
  ];
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // États pour les filtres
  const [typeProduit, setTypeProduit] = useState('');
  const [actif, setActif] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // État pour le zoom (gardé pour les colonnes qui utilisent zoomLevel dans leurs styles)
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('produits-pharmaceutiques-zoom-level');
    return saved ? parseFloat(saved) : 1.0;
  });

  useEffect(() => {
    localStorage.setItem('produits-pharmaceutiques-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  useEffect(() => {
    const fetchProduits = async () => {
      try {
        const params = new URLSearchParams();
        if (typeProduit) params.append('type_produit', typeProduit);
        if (actif !== '') params.append('actif', actif);
        if (searchTerm) params.append('search', searchTerm);

        const response = await api.get("/api/produits-pharmaceutiques/", { params });
        setProduits(response.data);
        setError(null);
        setLoading(false);
      } catch (err) {
        setError("Impossible de charger les produits pharmaceutiques.");
        setLoading(false);
      }
    };
    fetchProduits();
  }, [typeProduit, actif, searchTerm]);


  const getTypeProduitLabel = useCallback((type) => {
    const option = TYPE_PRODUIT_OPTIONS.find(opt => opt.value === type);
    return option ? option.label : type;
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: "nom",
        headerName: "Nom",
        flex: 1,
        minWidth: 200,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: "type_produit",
        headerName: "Type",
        width: 150,
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => getTypeProduitLabel(value),
      },
      {
        field: "unite",
        headerName: "Unité",
        width: 120,
        headerAlign: 'center',
        align: 'center',
      },
      {
        field: "description",
        headerName: "Description",
        flex: 1,
        minWidth: 250,
        headerAlign: 'left',
        align: 'left',
      },
      {
        field: "actif",
        headerName: "Statut",
        width: 100,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params) => (
          <span className={params.value ? "status-actif" : "status-inactif"}>
            {params.value ? "Actif" : "Inactif"}
          </span>
        ),
      },
    ];

    const canEdit = user?.is_admin || hasPermission(user, PERMISSIONS.CHANGE_PHARMACIE);
    
    const adminColumns = canEdit
      ? [
          {
            field: "edit",
            headerName: "Actions",
            width: 130,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
              <button
                onClick={() => navigate(`/produits-pharmaceutiques/${params.row.id}/modifier`)}
                className="grid-action-button"
                style={{ fontSize: `${1.3 * zoomLevel}rem` }}
              >
                Modifier
              </button>
            ),
          },
        ]
      : [];

    return [...baseColumns, ...adminColumns];
  }, [user, navigate, zoomLevel, getTypeProduitLabel]);

  return (
    <div className="produits-pharmaceutiques-page">
      <div className="title-badge">
        <FaPills className="page-title-icon" aria-hidden="true" />
        <span>CATALOGUE DES PRODUITS PHARMACEUTIQUES</span>
      </div>
      {(user?.is_admin || hasPermission(user, PERMISSIONS.ADD_PHARMACIE)) && (
        <div className="action-buttons-container">
          <button
            type="button"
            className="save-but"
            onClick={() => navigate("/ajouter-produit-pharmaceutique")}
          >
            <FaPlus />
            <span>{t.addProduct}</span>
          </button>
        </div>
      )}
      {loading && <p>Chargement...</p>}
      {error && <p className="error">{error}</p>}

      {!loading && !error && (
        <div className="produits-pharmaceutiques-container">
          <div className="top-section">
            <SearchBar
              placeholder={t.searchByProduct}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="compact"
            />
          </div>
          <FiltersContainer title={t.filters}>
            <FilterSelect
              name="typeProduit"
              value={typeProduit}
              onChange={(e) => setTypeProduit(e.target.value)}
              options={TYPE_PRODUIT_OPTIONS}
              placeholder={t.filterByType}
              className="filtre-type"
              width="200px"
            />
            <FilterSelect
              name="actif"
              value={actif}
              onChange={(e) => setActif(e.target.value)}
              options={[
                { value: "true", label: "Actif" },
                { value: "false", label: "Inactif" }
              ]}
              placeholder={t.filterByStatus}
              className="filtre-actif"
              width="180px"
            />
          </FiltersContainer>

          {/* DataGrid */}
          {produits.length === 0 ? (
            <div className="empty-message-container">
              <p className="empty-message">
                {typeProduit || actif || searchTerm
                  ? "Aucun produit trouvé avec les filtres sélectionnés."
                  : "Aucun produit pharmaceutique enregistré."}
              </p>
            </div>
          ) : (
            <DataGridTable
              rows={produits}
              columns={columns}
              loading={loading}
              storageKey="produits-pharmaceutiques-zoom-level"
              zoomLevel={zoomLevel}
              onZoomChange={setZoomLevel}
              initialSort={{ field: 'nom', sort: 'asc' }}
              pageSize={25}
              containerClassName="produits-grid-container"
            />
          )}
        </div>
      )}
    </div>
  );
}